import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

interface FormItem {
  label: string;
  url: string;
  desc?: string;
  extra?: { label: string; url: string }[];
}

interface AccordionSection {
  title: string;
  forms: FormItem[];
  note?: string;
}

interface FormCategory {
  id: string;
  title: string;
  icon: string;
  color: string;
  sections: AccordionSection[];
  intro?: string;
}

const CATEGORIES: FormCategory[] = [
  {
    id: 'driver-license',
    title: 'Driver License Forms',
    icon: 'ri-id-card-line',
    color: '#1d4ed8',
    intro: 'Visit Commercial Driver Licensing for CDL forms.',
    sections: [
      {
        title: 'Applications',
        forms: [
          { label: 'Application for Driving Privileges or ID Card (DMV 002)', url: 'https://dmv.nv.gov/pdfforms/dmv002en.pdf', desc: 'Do not mail this form! Mailed applications are rejected.', extra: [{ label: 'Spanish', url: 'https://dmv.nv.gov/pdfforms/dmv002es.pdf' }, { label: 'Tagalog', url: 'https://dmv.nv.gov/pdfforms/dmv002tl.pdf' }] },
          { label: 'Application for Driving Privilege or ID Card By Mail (DMV 204)', url: 'https://dmv.nv.gov/pdfforms/dmv204en.pdf', desc: 'For drivers currently licensed in Nevada who meet eligibility requirements to complete transactions by mail. Cannot be used for original issuance.', extra: [{ label: 'Spanish', url: 'https://dmv.nv.gov/pdfforms/dmv204es.pdf' }, { label: 'Tagalog', url: 'https://dmv.nv.gov/pdfforms/dmv204tl.pdf' }] },
          { label: 'Change of Address Notification by Mail (DMV 22)', url: 'https://dmv.nv.gov/pdfforms/dmv22en.pdf', desc: 'Change your address online. See Address Changes.', extra: [{ label: 'Spanish', url: 'https://dmv.nv.gov/pdfforms/dmv22es.pdf' }, { label: 'Tagalog', url: 'https://dmv.nv.gov/pdfforms/dmv22tl.pdf' }] },
          { label: 'Minor Affidavit (DP 38)', url: 'https://dmv.nv.gov/pdfforms/dp38.pdf', desc: 'Certifies that a minor is aware of various requirements and laws. Also used by a parent/guardian to cancel a minor driver license or instruction permit.' },
          { label: 'Certification of Attendance (DMV 301)', url: 'https://dmv.nv.gov/pdfforms/dmv301.pdf', desc: 'For drivers under 18 to show proof of school attendance.' },
          { label: 'Parent/Guardian Consent for Minors (DMV 302)', url: 'https://dmv.nv.gov/pdfforms/dmv302.pdf', desc: 'Drivers under 18 will need parent/guardian consent for instruction permit/licensing privilege.' },
          { label: 'Beginning Driver Experience Log (DLD 130)', url: 'https://dmv.nv.gov/pdfforms/dld130.pdf', desc: 'For certification of Behind-the-Wheel Driving Experience for drivers under 18.', extra: [{ label: 'Additional Time Sheets', url: 'https://dmv.nv.gov/pdfforms/dld130a.pdf' }] },
          { label: 'Driver Certification of Eligibility — Farm/Military (DLD 121)', url: 'https://dmv.nv.gov/pdfforms/dld121.pdf', desc: 'To claim exemption from federal commercial licensing regulations.' },
          { label: 'Driver Certification of Eligibility — Firefighters (DP 046)', url: 'https://dmv.nv.gov/pdfforms/dp046.pdf', desc: 'To claim exemption from federal commercial licensing regulations.' },
          { label: 'Declaration of Homeless Status (DMV 128)', url: 'https://dmv.nv.gov/pdfforms/dmv128.pdf', desc: 'For the homeless to apply for a one-time waiver for a duplicate license or ID card.' },
          { label: 'Request for Waiver of Duplicate Fees — Released Prisoners (DP 190)', url: 'https://dmv.nv.gov/pdfforms/dp190.pdf', desc: 'For recently-released prisoners to apply for a one-time waiver for a duplicate license or ID card.' },
          { label: "Released Prisoner or Inmate Driver's License and ID Card Information (DMV 013)", url: 'https://dmv.nv.gov/pdfforms/dmv013.pdf', desc: 'Proof of identity and other requirements for prisoners recently released from a Nevada institution.' },
          { label: 'Application for Alternate Address (DMV 007)', url: 'https://dmv.nv.gov/pdfforms/dmv007.pdf', desc: 'For applicants authorized by law to list an alternate address on their license.' },
          { label: 'Drive Test Waiver for Foreign Nationals (DMV 015)', url: 'https://dmv.nv.gov/pdfforms/dmv015.pdf', desc: 'For licensed drivers from Taiwan or South Korea moving to Nevada.' },
          { label: 'Application for Expedited Service Permit (SP 59)', url: 'https://dmv.nv.gov/pdfforms/sp59.pdf', desc: 'For disabled individuals.' },
          { label: 'Replacement IFTA License/Decal Form (NRS 485.265)', url: 'https://dmv.nv.gov/pdfforms/mc097.pdf', desc: 'For those licensed under the Nevada International Fuel Tax Agreement.' },
          { label: 'Longer Combination Vehicle (LCV) Permit Application/Replacement Form', url: 'https://dmv.nv.gov/pdfforms/mc098.pdf', desc: 'For Reducible Vehicle/Load Combinations Exceeding 70 Feet in Length.' },
          { label: 'Standard and Personalized Electric Vehicle License Plates Form (SP 98)', url: 'https://dmv.nv.gov/pdfforms/sp98.pdf', desc: 'For standard and personalized license plates for passenger vehicles, light commercial vehicles and motorcycles wholly powered by an electric motor.' },
        ],
      },
      {
        title: 'Medical',
        forms: [
          { label: 'Confidential Physicians Report (DLD 7)', url: 'https://dmv.nv.gov/pdfforms/dld7.pdf', desc: "For medical assessments of a motorist's driving ability." },
          { label: 'Eye Examination Certificate (DP 18)', url: 'https://dmv.nv.gov/pdfforms/dp18.pdf', desc: 'For visual examinations completed by a physician.' },
          { label: 'Application For Approval To Drive With Bioptic Lenses (DLD 98)', url: 'https://dmv.nv.gov/pdfforms/dld98.pdf', desc: 'For licensed vision specialists who dispense bioptic devices.' },
          { label: 'Agency Request for Re-Examination (DLD 23)', url: 'https://dmv.nv.gov/pdfforms/dld23.pdf', desc: 'For law enforcement, government agencies and other institutions to request a motorist undergo driving tests.' },
          { label: 'Request for Re-Evaluation (DLD 23a)', url: 'https://dmv.nv.gov/pdfforms/dld23a.pdf', desc: 'For relatives to request a motorist undergo driving tests.' },
        ],
      },
      {
        title: 'Restricted Licenses, Suspensions and Revocations',
        forms: [
          { label: 'Application for Restricted License (DMV 21)', url: 'https://dmv.nv.gov/pdfforms/dmv21.pdf', desc: 'To apply for a standard restricted license during a license revocation, for medical hardship, school transportation or child visitation.' },
          { label: '24/7 Restricted License Application (DMV 247)', url: 'https://dmv.nv.gov/pdfforms/dmv247.pdf', desc: 'To apply for a restricted license for those ordered to participate in the 24/7 Sobriety and Drug Monitoring Program.' },
          { label: 'One Year Default Affidavit (DLD 33)', url: 'https://dmv.nv.gov/pdfforms/dld33.pdf', desc: 'For drivers whose license is suspended under financial responsibility laws.' },
          { label: 'Two Year Default Affidavit (DLD 93)', url: 'https://dmv.nv.gov/pdfforms/dld93.pdf', desc: 'For drivers whose license is suspended under financial responsibility laws.' },
          { label: 'Statement of Surrender of Nevada Driver License and Vehicle Registration (DLD 95)', url: 'https://dmv.nv.gov/pdfforms/dld95.pdf', desc: 'For drivers whose license is suspended under financial responsibility laws.' },
          { label: 'Voluntary License Identification Card Surrender (DP 5)', url: 'https://dmv.nv.gov/pdfforms/dp5.pdf', desc: 'For drivers who are surrendering their license for insurance, medical or other reasons.' },
        ],
      },
      {
        title: 'Residency',
        note: 'See Proof of Identity and Residency for the documents you need to apply for an original license or ID.',
        forms: [
          { label: 'Certification of Nevada Residency (DMV 005)', url: 'https://dmv.nv.gov/pdfforms/dmv005.pdf', desc: "To enable another person to provide proof of an applicant's residency." },
          { label: 'Relief Agency or Shelter Certification (DMV 115)', url: 'https://dmv.nv.gov/pdfforms/dmv115.pdf', desc: "To enable social service agencies to provide proof of the applicant's residency." },
          { label: 'Property Owner Residency Affidavit (DMV 116)', url: 'https://dmv.nv.gov/pdfforms/dmv116.pdf', desc: "To enable a property owner to provide proof of the applicant's residency." },
        ],
      },
    ],
  },
  {
    id: 'vehicle-registration',
    title: 'Vehicle Registration Forms',
    icon: 'ri-car-line',
    color: '#059669',
    sections: [
      {
        title: 'Vehicle Registration',
        forms: [
          { label: 'Change of Address Notification by Mail (DMV 22)', url: 'https://dmv.nv.gov/pdfforms/dmv22en.pdf', desc: 'Change your address online. See Address Changes.', extra: [{ label: 'Spanish', url: 'https://dmv.nv.gov/pdfforms/dmv22es.pdf' }, { label: 'Tagalog', url: 'https://dmv.nv.gov/pdfforms/dmv22tl.pdf' }] },
          { label: 'Application for Duplicate Registration Certificate (VP 013)', url: 'https://dmv.nv.gov/pdfforms/vp013.pdf', desc: '$5 fee for each certificate requested. You may mail this application and a check, money order or Payment Authorization to the address on the form.' },
          { label: 'Change of Address Application (MC 32)', url: 'https://dmv.nv.gov/pdfforms/mc032.pdf', desc: 'Needed to renew or change a license or registration. Must be signed by the owner.' },
          { label: 'Application for Vehicle Registration (VP 222)', url: 'https://dmv.nv.gov/pdfforms/vp222.pdf', desc: 'Needed when applying for an original registration. Must be signed by the owner.' },
          { label: 'Affidavit of Non-Operation (VP 018)', url: 'https://dmv.nv.gov/pdfforms/vp018.pdf', desc: 'Certifies that a specific vehicle has not been operated on Nevada public highways.' },
          { label: 'Vehicle Inspection Certificate (VP 015)', url: 'https://dmv.nv.gov/pdfforms/vp015.pdf', desc: 'For Vehicle Identification Number Verification. Must be completed by a DMV representative, a peace officer or other authorized agent.' },
          { label: 'Moped Registration Application/Inspection (VP 30)', url: 'https://dmv.nv.gov/pdfforms/vp30.pdf', desc: 'Do not mail this form. The inspection must be completed by a DMV representative, Sheriff or Deputy Sheriff.' },
          { label: 'Emission Control Exemption Application (EC 008)', url: 'https://dmv.nv.gov/pdfforms/ec008.pdf', desc: 'For Nevada residents temporarily out of state who are unable to obtain a smog check.' },
          { label: 'Odometer Certification for Emission Exemption (EC 018)', url: 'https://dmv.nv.gov/pdfforms/ec018.pdf', desc: 'To claim the emission inspection exemption for vehicles registered as a Classic Rod or Classic Vehicle.' },
          { label: 'Affidavit of Non-Residency (VP 198)', url: 'https://dmv.nv.gov/pdfforms/vp198.pdf', desc: 'To register vehicles which were purchased in Nevada, taken out-of-state and then returned to Nevada.' },
          { label: 'License Plate Hearing Request (SP 75)', url: 'https://dmv.nv.gov/pdfforms/sp75.pdf', desc: 'To request an Administrative Hearing for a denied or recalled personalized license plate number.' },
          { label: 'Registration Service Transmittal Form (VP 193)', url: 'https://dmv.nv.gov/pdfforms/vp193.pdf', desc: 'Note: All document preparation services are required to be registered with the Nevada Secretary of State.', extra: [{ label: 'Registration Service Transaction Request (VP 247)', url: 'https://dmv.nv.gov/pdfforms/vp247.pdf' }] },
          { label: 'Application for Non-Resident Business Permit (VP 264)', url: 'https://dmv.nv.gov/pdfforms/vp264.pdf', desc: 'For commercial vehicles under 10,001 pounds.' },
          { label: 'Military Surplus Vehicle Inspection (VP 017)', url: 'https://dmv.nv.gov/pdfforms/VP-017.pdf', desc: 'For vehicles labeled for "OFF-ROAD USE ONLY".' },
        ],
      },
      {
        title: 'Fees and Tax Exemptions',
        note: 'See Nevada County Assessors to apply for tax exemptions for veterans, surviving spouses and the blind.',
        forms: [
          { label: 'Registration Fee Refund Request Form (VP 210)', url: 'https://dmv.nv.gov/pdfforms/vp210.pdf', desc: 'To request a refund under certain limited circumstances.' },
          { label: 'Designation of Use for Veterans Exemption (VP 187)', url: 'https://dmv.nv.gov/pdfforms/vp187.pdf', desc: 'Used to designate that a portion of a Veterans Exemption be directed to the Nevada Veterans Home Account.' },
          { label: 'Affidavit for Transfer of Exemption to Spouse (VP 259)', url: 'https://dmv.nv.gov/pdfforms/vp259.pdf', desc: "Used to transfer a Veterans Exemption to the veteran's spouse." },
          { label: 'Trust Affidavit for Exemptions (VP 242)', url: 'https://dmv.nv.gov/pdfforms/vp242.pdf', desc: 'To enable the use of a Veterans or other exemption when the vehicle is titled to a trust.' },
          { label: 'Active Duty Military Combat Affidavit (VP 258)', url: 'https://dmv.nv.gov/pdfforms/vp258.pdf', desc: 'To claim an exemption from registration late fees.' },
          { label: 'Non-Resident, Active Duty Military Governmental Services Tax Exemption Affidavit (VP 203)', url: 'https://dmv.nv.gov/pdfforms/vp203.pdf', desc: 'For active duty military members who are residents of another state and stationed in Nevada.' },
          { label: 'Non-Resident Military Spouse Governmental Services Tax Exemption Affidavit (VP 203s)', url: 'https://dmv.nv.gov/pdfforms/vp203s.pdf', desc: 'For spouses of active duty military members who are residents of another state and stationed in Nevada.' },
          { label: 'Application for Governmental Services Tax Exemption (VP 154)', url: 'https://dmv.nv.gov/pdfforms/vp154.pdf', desc: 'For Nevada Native American Tribal Members residing on tribal lands.' },
          { label: 'Farmer/Rancher Affidavit (VP 159)', url: 'https://dmv.nv.gov/pdfforms/vp159.pdf', desc: 'Used to adjust registration fees on large farm vehicles.' },
        ],
      },
      {
        title: 'License Plates',
        note: 'See License Plates for all plate-related forms.',
        forms: [
          { label: 'Lost, Stolen or Mutilated License Plate Affidavit (VP 202)', url: 'https://dmv.nv.gov/pdfforms/vp202.pdf', desc: 'You must obtain new plates in person at a DMV Full Service Office.' },
          { label: 'Duplicate License Plate Application (SP 14)', url: 'https://dmv.nv.gov/pdfforms/sp14.pdf', desc: 'Plates to replace damaged or unreadable plates in the same style. Not for stolen plates.' },
          { label: 'Application for Disabled Persons License Plates/Placards (SP 27)', url: 'https://dmv.nv.gov/pdfforms/sp27.pdf', desc: '' },
        ],
      },
      {
        title: 'Insurance',
        note: 'Most insurance issues can be handled online or by phone without a DMV office visit.',
        forms: [
          { label: 'Vehicle Registration Insurance Suspension Guide (NVL 010)', url: 'https://dmv.nv.gov/pdfforms/nvl010.pdf', desc: 'Items to check if the DMV is reporting a lapse in your insurance coverage.' },
          { label: 'Declaration of Responsibility (NVL 019)', url: 'https://dmv.nv.gov/pdfforms/nvl019.pdf', desc: 'To declare that you will meet financial responsibility requirements.' },
          { label: 'Dormant Vehicle Affidavit (NVL 003)', url: 'https://dmv.nv.gov/pdfforms/nvl003.pdf', desc: 'Used to reinstate a vehicle registration which has been suspended for failure to maintain liability insurance. Only used if the vehicle has not been operated during the lapse of insurance.' },
          { label: 'Property Owner Affidavit (NVL 025)', url: 'https://dmv.nv.gov/pdfforms/nvl025.pdf', desc: 'To declare that a vehicle was stored at a specific address.' },
          { label: 'Insurance Hearing Request (NVL 005)', url: 'https://dmv.nv.gov/pdfforms/nvl005.pdf', desc: 'To request an Administrative Hearing for an insurance-related vehicle registration suspension.' },
        ],
      },
    ],
  },
  {
    id: 'vehicle-ownership',
    title: 'Vehicle Ownership Forms',
    icon: 'ri-file-transfer-line',
    color: '#7c3aed',
    sections: [
      {
        title: 'Vehicle Ownership',
        forms: [
          { label: 'Bill Of Sale (VP 104)', url: 'https://dmv.nv.gov/pdfforms/vp104.pdf', desc: "Releases a private party's ownership interest in a vehicle." },
          { label: 'Application for Duplicate Nevada Certificate of Title (VP 012)', url: 'https://dmv.nv.gov/pdfforms/vp012.pdf', desc: 'This form is for use only if the vehicle was last titled in Nevada. If your vehicle is titled in another state, you must obtain a duplicate from that state.' },
          { label: 'Lien Release (VP 186)', url: 'https://dmv.nv.gov/pdfforms/vp186.pdf', desc: 'Releases an ownership interest held by a financial institution or other third party.' },
          { label: 'Electronic Lienholder Reassignment Affidavit (VP 268)', url: 'https://dmv.nv.gov/pdfforms/vp268.pdf', desc: 'Assigns lienholder rights on electronic titles.' },
          { label: 'Attempt to Contact (VP 212)', url: 'https://dmv.nv.gov/pdfforms/vp212.pdf', desc: 'Instructions for attempting to contact the owner of a vehicle.' },
          { label: 'Lost Title Affidavit (VP 206)', url: 'https://dmv.nv.gov/pdfforms/vp206.pdf', desc: 'Certifies that a recently-issued title was not received.' },
          { label: 'Power of Attorney (VP 136)', url: 'https://dmv.nv.gov/pdfforms/vp136.pdf', desc: 'Used to convey the authority to transfer the ownership of or register a vehicle.' },
          { label: 'Affidavit of Acknowledgment for a Power of Attorney (VP 249)', url: 'https://dmv.nv.gov/pdfforms/vp249.pdf', desc: 'To certify a true copy of a Power of Attorney.' },
          { label: 'Erasure Affidavit (VP 019)', url: 'https://dmv.nv.gov/pdfforms/vp019.pdf', desc: 'Used to state reasons for changes and errors in completing the buyer or reassignment sections of vehicle titles.' },
          { label: 'One and the Same Affidavit (VP 185)', url: 'https://dmv.nv.gov/pdfforms/vp185.pdf', desc: 'Used to clarify the identity of a vehicle owner.' },
        ],
      },
      {
        title: 'Estates and Trusts',
        forms: [
          { label: 'Affidavit for Transfer of Title for Estates Without Probate (VP 024)', url: 'https://dmv.nv.gov/pdfforms/vp024.pdf', desc: "For a deceased person's heirs, estate or creditors to claim ownership of a vehicle." },
          { label: 'Trustee Appointment and Powers Affidavit (VP 188)', url: 'https://dmv.nv.gov/pdfforms/vp188.pdf', desc: 'To declare a person has trustee powers to transfer ownership of a vehicle.' },
          { label: 'Transfer on Death Application (VP 239)', url: 'https://dmv.nv.gov/pdfforms/vp239.pdf', desc: 'To add, change or delete a beneficiary designated to receive a vehicle upon death of the owner.' },
          { label: "Transfer on Death — Beneficiary's Affidavit for Title (VP 241)", url: 'https://dmv.nv.gov/pdfforms/vp241.pdf', desc: 'For a beneficiary to claim ownership of a vehicle when the original title is not available.' },
          { label: 'Moped / Motorcycle Declaration (VP 238)', url: 'https://dmv.nv.gov/pdfforms/vp238.pdf', desc: 'To certify that a vehicle is a motorcycle after a determination that it does not meet the specifications of a moped.' },
        ],
      },
      {
        title: 'Publications',
        forms: [
          { label: 'Registration and Title Guide', url: 'https://dmv.nv.gov/pdfforms/regtitle.pdf', desc: 'Instructions for auto dealers and other licensees.' },
          { label: 'Wrecker and Tow Car Guide', url: 'https://dmv.nv.gov/pdfforms/wreckertow.pdf', desc: '' },
          { label: 'Salvage Title and Non-Repairable Vehicle Certificate Guide', url: 'https://dmv.nv.gov/pdfforms/salvageguide.pdf', desc: '' },
          { label: 'Off-Highway Vehicle Registration and Title Guide', url: 'https://dmv.nv.gov/pdfforms/ohvregtitle.pdf', desc: '' },
          { label: 'Motor Vehicle Equipment Requirements', url: 'https://dmv.nv.gov/pdfforms/equipreqmv.pdf', desc: '', extra: [{ label: 'Trailer Equipment Requirements', url: 'https://dmv.nv.gov/pdfforms/equipreqtr.pdf' }, { label: 'Motorcycle Equipment Requirements', url: 'https://dmv.nv.gov/pdfforms/equipreqmc.pdf' }] },
        ],
      },
      {
        title: 'Salvage, Rebuilt, Constructed and Non-Repairable Vehicles',
        forms: [
          { label: 'Application for Salvage Title or Non-Repairable Vehicle Certificate (VP 213)', url: 'https://dmv.nv.gov/pdfforms/vp213.pdf', desc: 'To issue these types of titles or to apply for a duplicate salvage title.' },
          { label: 'Application for Non-Repairable Vehicle Certificate (VP 260)', url: 'https://dmv.nv.gov/pdfforms/vp260.pdf', desc: 'For use only by a licensed automobile wrecker, dismantler, or recycler.' },
          { label: 'Insurance Company Affidavit Without Endorsed Title (VP 236)', url: 'https://dmv.nv.gov/pdfforms/vp236.pdf', desc: 'To issue a title when vehicle owner(s) cannot be contacted.' },
          { label: 'Donation Affidavit without Endorsed Title (VP 275)', url: 'https://dmv.nv.gov/pdfforms/vp275.pdf', desc: 'For non-profit organizations when vehicle owner(s) cannot be contacted.' },
          { label: 'Authorization for Vehicle Restoration (VP 209)', url: 'https://dmv.nv.gov/pdfforms/vp209.pdf', desc: 'To request DMV authorization to restore a vehicle.' },
          { label: 'Certificate of Inspection/Affidavit of Construction (VP 064)', url: 'https://dmv.nv.gov/pdfforms/vp064.pdf', desc: 'To certify the safety of rebuilt, reconstructed or assembled vehicles.' },
          { label: 'Certificate of Inspection for Rebuilt Vehicles — Not Salvage (VP 064A)', url: 'https://dmv.nv.gov/pdfforms/vp064a.pdf', desc: 'To certify the safety of vehicles that have had certain repairs.' },
          { label: 'Certificate of Inspection/Affidavit of Construction — Motorcycles (VP 064M)', url: 'https://dmv.nv.gov/pdfforms/vp064m.pdf', desc: 'To certify the safety of rebuilt, reconstructed or assembled motorcycles.' },
          { label: 'Motorcycle Highway Use Affidavit (VP 254)', url: 'https://dmv.nv.gov/pdfforms/vp254.pdf', desc: 'To certify the safety of an off-road motorcycle that has been converted to on-road use.' },
          { label: 'Certificate of Inspection/Affidavit of Construction — Trailers (VP 223)', url: 'https://dmv.nv.gov/pdfforms/vp223.pdf', desc: 'To certify the safety of rebuilt, reconstructed or assembled trailers.' },
          { label: 'Abandoned Vehicle Safety Affidavit (VP 279)', url: 'https://dmv.nv.gov/pdfforms/vp279.pdf', desc: 'To be used when submitting a lien sale packet for an abandoned vehicle and bypass the salvage title requirements.' },
        ],
      },
      {
        title: 'Repossessions and Liens',
        forms: [
          { label: 'Repossession Affidavit (VP 020)', url: 'https://dmv.nv.gov/pdfforms/vp020.pdf', desc: '' },
          { label: 'Lien Sale Requirements (VP 263)', url: 'https://dmv.nv.gov/pdfforms/vp263.pdf', desc: '' },
          { label: 'Lien Sale Affidavit (VP 147)', url: 'https://dmv.nv.gov/pdfforms/vp147.pdf', desc: '' },
          { label: 'Lien Sale Registration Certification (VP 201)', url: 'https://dmv.nv.gov/pdfforms/vp201.pdf', desc: '' },
          { label: 'Letter of Transmittal for Abandoned Junk Vehicles (VP 162)', url: 'https://dmv.nv.gov/pdfforms/vp162.pdf', desc: '' },
          { label: 'Abandoned Vehicle Lien Transfer (VP 262)', url: 'https://dmv.nv.gov/pdfforms/vp262.pdf', desc: '' },
          { label: 'Abandoned Recreational Vehicle Title Affidavit (VP 267)', url: 'https://dmv.nv.gov/pdfforms/vp267.pdf', desc: '' },
          { label: 'Contest Lien Sale Validity (VP 277)', url: 'https://dmv.nv.gov/pdfforms/vp277.pdf', desc: 'To notify DMV that a vehicle lien is being contested in a Justice Court.' },
        ],
      },
    ],
  },
  {
    id: 'miscellaneous',
    title: 'Miscellaneous Forms',
    icon: 'ri-file-list-3-line',
    color: '#b45309',
    sections: [
      {
        title: 'Complaints',
        note: 'The DMV Compliance Enforcement Division (CED) accepts complaints against auto dealers, lessors, emissions stations, driving schools and other businesses regulated by the DMV.',
        forms: [
          { label: 'Personalized License Plate Complaint Form (SP 74)', url: 'https://dmv.nv.gov/pdfforms/sp74.pdf', desc: 'See Nevada Administrative Code 482.230.' },
          { label: 'Fuel Dealer Complaint Form (MC 083)', url: 'https://dmv.nv.gov/pdfforms/mc083.pdf', desc: 'For consumers who wish to file a complaint against a retail station dispensing LPG or CNG.' },
          { label: 'Potential Fuel Tax Evasion Report', url: 'https://dmv.nv.gov/fuel_report.htm', desc: '' },
          { label: 'Smog Spotter Smoking Vehicle Report', url: 'https://dmv.nv.gov/smogspotter/index.htm', desc: '', extra: [{ label: 'Spanish Version', url: 'https://dmv.nv.gov/smogspotter/index-es.htm' }] },
        ],
      },
      {
        title: 'Crashes',
        forms: [
          { label: 'Report of Traffic Crash (SR 1)', url: 'https://dmv.nv.gov/pdfforms/sr1.pdf', desc: 'You must file this form within 10 days of any accident which was: (1) not investigated at the scene by law enforcement and (2) caused injury, death or more than $750 damage.' },
          { label: 'Failure to Report Crash Affidavit (DLD 68)', url: 'https://dmv.nv.gov/pdfforms/dld68.pdf', desc: '' },
        ],
      },
      {
        title: 'Window Tinting',
        forms: [
          { label: 'Application for Window Tint Exemption (DO 200)', url: 'https://dmv.nv.gov/pdfforms/do200.pdf', desc: 'For ambulance, hearse, law enforcement or medical exemption and those who qualify to exceed the maximum window tinting standards.' },
        ],
      },
    ],
  },
];

function highlight(text: string, query: string) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded px-0.5" style={{ backgroundColor: '#fef08a', color: '#1e293b' }}>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function AccordionItem({ section, defaultOpen, searchQuery, catColor }: {
  section: AccordionSection;
  defaultOpen: boolean;
  searchQuery: string;
  catColor: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const prevDefault = useRef(defaultOpen);

  useEffect(() => {
    if (prevDefault.current !== defaultOpen) {
      setOpen(defaultOpen);
      prevDefault.current = defaultOpen;
    }
  }, [defaultOpen]);

  const visibleForms = searchQuery
    ? section.forms.filter(f =>
        f.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (f.desc && f.desc.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : section.forms;

  if (searchQuery && visibleForms.length === 0) return null;

  return (
    <div className="rounded-xl overflow-hidden transition-all" style={{ border: '1px solid #e2e8f0' }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer transition-colors"
        style={{ backgroundColor: open ? '#f8fafc' : '#fff' }}
      >
        <span className="font-semibold text-sm flex items-center gap-2.5" style={{ color: '#1e293b' }}>
          <span className="w-1.5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: catColor }}></span>
          {section.title}
          <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}>
            {visibleForms.length} form{visibleForms.length !== 1 ? 's' : ''}
          </span>
        </span>
        <i className={`ri-arrow-down-s-line text-xl flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          style={{ color: '#94a3b8' }}></i>
      </button>

      {open && (
        <div className="border-t" style={{ borderColor: '#f1f5f9' }}>
          {section.note && (
            <div className="px-5 py-3 text-xs text-gray-500 italic flex items-start gap-2" style={{ backgroundColor: '#fffbeb', borderBottom: '1px solid #fef3c7' }}>
              <i className="ri-information-line flex-shrink-0 mt-0.5" style={{ color: '#d97706' }}></i>
              {section.note}
            </div>
          )}
          <ul className="divide-y" style={{ borderColor: '#f8fafc' }}>
            {visibleForms.map((form, i) => (
              <li key={i} className="px-5 py-3.5 flex items-start gap-3 hover:bg-slate-50 transition-colors group/form">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors"
                  style={{ backgroundColor: '#f1f5f9' }}>
                  <i className="ri-file-pdf-2-line text-sm" style={{ color: '#E87722' }}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <a href={form.url} target="_blank" rel="noopener noreferrer nofollow"
                    className="text-sm font-semibold hover:underline leading-snug block"
                    style={{ color: '#002855' }}>
                    {highlight(form.label, searchQuery)}
                  </a>
                  {form.extra && form.extra.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {form.extra.map((e, j) => (
                        <a key={j} href={e.url} target="_blank" rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full hover:underline transition-colors"
                          style={{ backgroundColor: '#EEF4FF', color: '#002855' }}>
                          <i className="ri-external-link-line text-xs"></i>{e.label}
                        </a>
                      ))}
                    </div>
                  )}
                  {form.desc && (
                    <p className="text-xs mt-1 leading-relaxed" style={{ color: '#64748b' }}>
                      {highlight(form.desc, searchQuery)}
                    </p>
                  )}
                </div>
                <a href={form.url} target="_blank" rel="noopener noreferrer nofollow"
                  className="flex-shrink-0 flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg opacity-0 group-hover/form:opacity-100 transition-all whitespace-nowrap"
                  style={{ backgroundColor: '#002855', color: '#fff' }}>
                  <i className="ri-download-line"></i>PDF
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function FormsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandKey, setExpandKey] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = 'Nevada DMV Forms and Publications | DMV-Xpress Services™ Las Vegas';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'All official Nevada DMV forms — Driver License, Vehicle Registration, Vehicle Ownership, and Miscellaneous. PDF downloads from dmv.nv.gov. DMV-Xpress Services™ Las Vegas NRS240A.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || ''}/forms`);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.setAttribute('data-page-schema', 'forms');
    schema.text = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || ''}/forms#webpage`,
        url: `${import.meta.env.VITE_SITE_URL || ''}/forms`,
        name: 'Nevada DMV Forms and Publications | DMV-Xpress Services™ Las Vegas',
        description: 'All official Nevada DMV forms — Driver License, Vehicle Registration, Vehicle Ownership, and Miscellaneous. PDF downloads from dmv.nv.gov.',
        isPartOf: { '@type': 'WebSite', url: import.meta.env.VITE_SITE_URL || '' },
        about: {
          '@type': 'GovernmentService',
          name: 'Nevada DMV Forms Library',
          provider: { '@type': 'GovernmentOrganization', name: 'Nevada Department of Motor Vehicles', url: 'https://dmv.nv.gov' },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Nevada DMV Forms by Category',
        description: 'Official Nevada DMV forms organized by service category — Driver License, Vehicle Registration, Vehicle Ownership, Miscellaneous.',
        numberOfItems: 4,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Driver License Forms', url: `${import.meta.env.VITE_SITE_URL || ''}/forms#driver-license` },
          { '@type': 'ListItem', position: 2, name: 'Vehicle Registration Forms', url: `${import.meta.env.VITE_SITE_URL || ''}/forms#vehicle-registration` },
          { '@type': 'ListItem', position: 3, name: 'Vehicle Ownership Forms', url: `${import.meta.env.VITE_SITE_URL || ''}/forms#vehicle-ownership` },
          { '@type': 'ListItem', position: 4, name: 'Miscellaneous Forms', url: `${import.meta.env.VITE_SITE_URL || ''}/forms#miscellaneous` },
        ],
      },
    ]);
    document.head.appendChild(schema);
    return () => {
      const existing = document.head.querySelector('script[data-page-schema="forms"]');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  const totalForms = useMemo(() =>
    CATEGORIES.reduce((acc, cat) => acc + cat.sections.reduce((a, s) => a + s.forms.length, 0), 0),
    []
  );

  const searchResults = useMemo(() => {
    if (!searchQuery) return [];
    const q = searchQuery.toLowerCase();
    const results: { catTitle: string; catId: string; sectionTitle: string; form: FormItem }[] = [];
    CATEGORIES.forEach(cat => {
      cat.sections.forEach(sec => {
        sec.forms.forEach(form => {
          if (form.label.toLowerCase().includes(q) || (form.desc && form.desc.toLowerCase().includes(q))) {
            results.push({ catTitle: cat.title, catId: cat.id, sectionTitle: sec.title, form });
          }
        });
      });
    });
    return results;
  }, [searchQuery]);

  const toggleAll = () => {
    const next = !allExpanded;
    setAllExpanded(next);
    setExpandKey(k => k + 1);
  };

  const filteredCategories = activeCategory === 'all'
    ? CATEGORIES
    : CATEGORIES.filter(c => c.id === activeCategory);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc', fontFamily: "'Inter', 'Source Sans Pro', sans-serif" }}>
      <Navbar />

      {/* ── HERO ── */}
      <section style={{ background: 'linear-gradient(135deg, #002855 0%, #003d82 60%, #004a99 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-white/60 hover:text-white transition-colors">DMV-Xpress Home</Link>
            <i className="ri-arrow-right-s-line text-white/30"></i>
            <span className="text-white/90 font-medium">Forms &amp; Publications</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full mb-5"
                style={{ backgroundColor: 'rgba(232,119,34,0.2)', color: '#fbbf24', border: '1px solid rgba(232,119,34,0.3)' }}>
                <i className="ri-shield-check-fill"></i>
                DMV-Xpress Services™ — Licensed NRS240A — NOT the Nevada DMV
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight" style={{ letterSpacing: '-0.02em' }}>
                Nevada DMV<br />
                <span style={{ color: '#E87722' }}>Forms &amp; Publications</span>
              </h1>
              <p className="text-white/70 text-base leading-relaxed mb-6 max-w-lg">
                Every official Nevada DMV form — organized by service, fully searchable, linking directly to{' '}
                <a href="https://dmv.nv.gov/dmvforms.htm" target="_blank" rel="noopener noreferrer nofollow"
                  className="text-white font-semibold underline underline-offset-2 hover:text-yellow-300 transition-colors">
                  dmv.nv.gov
                </a>.
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                {[
                  { n: totalForms + '+', l: 'Official Forms' },
                  { n: '4', l: 'Categories' },
                  { n: 'PDF', l: 'Direct Downloads' },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-xl"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <span className="text-xl font-black text-white">{s.n}</span>
                    <span className="text-white/50 text-xs">{s.l}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-xl p-4 flex items-start gap-3"
                style={{ backgroundColor: 'rgba(232,119,34,0.12)', border: '1px solid rgba(232,119,34,0.25)' }}>
                <i className="ri-information-fill flex-shrink-0 mt-0.5" style={{ color: '#E87722' }}></i>
                <p className="text-white/70 text-sm leading-relaxed">
                  <strong className="text-white">Many forms require a Notary Public or DMV representative signature.</strong>{' '}
                  Fill out in advance, but do not sign until you are at the Notary or DMV Office.
                  <strong className="text-white ml-1">DMV-Xpress provides on-site notarization.</strong>
                </p>
              </div>
            </div>

            {/* Search + quick help */}
            <div className="space-y-4">
              {/* Live search */}
              <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}>
                <p className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-search-line" style={{ color: '#E87722' }}></i>
                  Search All Forms
                </p>
                <div className="relative">
                  <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search by form name, number, or keyword..."
                    className="w-full pl-10 pr-10 py-3 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2"
                    style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0' }}
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                      <i className="ri-close-circle-fill text-lg"></i>
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <p className="text-white/50 text-xs mt-2">
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
                  </p>
                )}
              </div>

              {/* Help card */}
              <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <p className="text-white font-bold text-sm mb-1 flex items-center gap-2">
                  <i className="ri-customer-service-2-fill" style={{ color: '#E87722' }}></i>
                  Need Help Filing?
                </p>
                <p className="text-white/60 text-xs leading-relaxed mb-3">
                  DMV-Xpress prepares and files all Nevada DMV forms on your behalf — correctly, the first time. No rejected applications.
                </p>
                <a href="tel:7028028696"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white font-bold text-sm transition-all whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #E87722 0%, #d06a18 100%)' }}>
                  <i className="ri-phone-fill"></i>Call 702.802.8696 — Book Appointment
                </a>
              </div>

              {/* Payment auth */}
              <div className="rounded-2xl p-4 flex items-center gap-3"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(232,119,34,0.2)' }}>
                  <i className="ri-bank-card-line" style={{ color: '#E87722' }}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <a href="https://dmv.nv.gov/pdfforms/adm205.pdf" target="_blank" rel="noopener noreferrer nofollow"
                    className="text-sm font-semibold text-white hover:underline block">
                    Payment Authorization Form (ADM 205)
                  </a>
                  <p className="text-white/40 text-xs">Authorizes DMV to charge your credit or debit card</p>
                </div>
                <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ backgroundColor: '#E87722', color: '#fff' }}>PDF</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SEARCH RESULTS ── */}
      {searchQuery && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold text-gray-900">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
            </h2>
            <button onClick={() => setSearchQuery('')}
              className="text-sm font-semibold flex items-center gap-1 cursor-pointer hover:underline"
              style={{ color: '#002855' }}>
              <i className="ri-close-line"></i>Clear search
            </button>
          </div>
          {searchResults.length === 0 ? (
            <div className="bg-white rounded-2xl border p-12 text-center" style={{ borderColor: '#e2e8f0' }}>
              <i className="ri-file-search-line text-5xl mb-4 block" style={{ color: '#cbd5e1' }}></i>
              <p className="font-semibold text-gray-700 mb-1">No forms found for &ldquo;{searchQuery}&rdquo;</p>
              <p className="text-gray-400 text-sm">Try a different keyword or browse by category below</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: '#e2e8f0' }}>
              {searchResults.map((r, i) => (
                <div key={i} className="flex items-start gap-4 px-5 py-4 border-b last:border-0 hover:bg-slate-50 transition-colors group/sr"
                  style={{ borderColor: '#f1f5f9' }}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: '#f1f5f9' }}>
                    <i className="ri-file-pdf-2-line" style={{ color: '#E87722' }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <a href={r.form.url} target="_blank" rel="noopener noreferrer nofollow"
                      className="text-sm font-semibold hover:underline block" style={{ color: '#002855' }}>
                      {highlight(r.form.label, searchQuery)}
                    </a>
                    {r.form.desc && (
                      <p className="text-xs mt-0.5 leading-relaxed" style={{ color: '#64748b' }}>
                        {highlight(r.form.desc, searchQuery)}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#EEF4FF', color: '#002855' }}>{r.catTitle}</span>
                      <span className="text-xs text-gray-400">{r.sectionTitle}</span>
                    </div>
                  </div>
                  <a href={r.form.url} target="_blank" rel="noopener noreferrer nofollow"
                    className="flex-shrink-0 flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg opacity-0 group-hover/sr:opacity-100 transition-all whitespace-nowrap"
                    style={{ backgroundColor: '#002855', color: '#fff' }}>
                    <i className="ri-download-line"></i>PDF
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── CATEGORY TABS + EXPAND ALL ── */}
      {!searchQuery && (
        <div className="sticky z-40 bg-white border-b" style={{ top: 68, borderColor: '#e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setActiveCategory('all')}
                className="flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-xl transition-all whitespace-nowrap cursor-pointer"
                style={{
                  backgroundColor: activeCategory === 'all' ? '#002855' : '#f1f5f9',
                  color: activeCategory === 'all' ? '#fff' : '#475569',
                }}>
                <i className="ri-apps-line"></i>All Forms
              </button>
              {CATEGORIES.map(cat => (
                <button key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className="flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-xl transition-all whitespace-nowrap cursor-pointer"
                  style={{
                    backgroundColor: activeCategory === cat.id ? cat.color : '#f1f5f9',
                    color: activeCategory === cat.id ? '#fff' : '#475569',
                  }}>
                  <i className={cat.icon}></i>
                  <span className="hidden sm:inline">{cat.title}</span>
                  <span className="sm:hidden">{cat.title.split(' ')[0]}</span>
                </button>
              ))}
            </div>
            <button onClick={toggleAll}
              className="flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-xl transition-all whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#f1f5f9', color: '#475569' }}>
              <i className={`ri-${allExpanded ? 'collapse' : 'expand'}-diagonal-line`}></i>
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
        </div>
      )}

      {/* ── FORM CATEGORIES ── */}
      {!searchQuery && (
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-14">
          {filteredCategories.map(cat => (
            <section key={cat.id} id={cat.id}>
              {/* Section header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: cat.color }}>
                  <i className={`${cat.icon} text-white text-xl`}></i>
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900 leading-none mb-2" style={{ letterSpacing: '-0.01em' }}>
                    {cat.title}
                  </h2>
                  <div className="flex items-center gap-2">
                    <div className="h-1 w-12 rounded-full" style={{ backgroundColor: '#E87722' }}></div>
                    {cat.intro && <p className="text-sm text-gray-500">{cat.intro}</p>}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold"
                  style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}>
                  <i className="ri-file-list-3-line"></i>
                  {cat.sections.reduce((a, s) => a + s.forms.length, 0)} forms
                </div>
              </div>

              <div className="space-y-2">
                {cat.sections.map((section, si) => (
                  <AccordionItem
                    key={`${cat.id}-${si}-${expandKey}`}
                    section={section}
                    defaultOpen={allExpanded}
                    searchQuery={searchQuery}
                    catColor={cat.color}
                  />
                ))}
              </div>
            </section>
          ))}

          {/* Bottom CTA */}
          <div className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, #002855 0%, #003d82 100%)' }}>
            <i className="ri-external-link-line text-3xl mb-3 block" style={{ color: '#E87722' }}></i>
            <h3 className="font-black text-xl text-white mb-2">Looking for a form not listed here?</h3>
            <p className="text-white/60 text-sm mb-5 max-w-md mx-auto">
              Visit the official Nevada DMV Forms &amp; Publications page for the complete and most up-to-date list of all available forms.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href="https://dmv.nv.gov/dmvforms.htm" target="_blank" rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 text-white font-bold px-6 py-3 rounded-xl text-sm transition-all whitespace-nowrap"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <i className="ri-government-line"></i>Visit dmv.nv.gov/dmvforms.htm
              </a>
              <a href="tel:7028028696"
                className="inline-flex items-center gap-2 text-white font-bold px-6 py-3 rounded-xl text-sm transition-all whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #E87722 0%, #d06a18 100%)' }}>
                <i className="ri-phone-fill"></i>Call Us — We Prepare Forms For You
              </a>
            </div>
          </div>
        </main>
      )}

      {/* ── FOOTER ── */}
      <footer style={{ background: 'linear-gradient(135deg, #002855 0%, #001a3d 100%)', borderTop: '3px solid #E87722' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #003d82, #002855)' }}>
                  <i className="ri-car-fill text-white text-lg"></i>
                </div>
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-white font-black text-base">DMV-Xpress</span>
                    <span className="text-xs font-bold" style={{ color: '#E87722' }}>Services™</span>
                  </div>
                  <p className="text-white/40 text-xs">Licensed Third-Party — NRS240A — Las Vegas, NV</p>
                </div>
              </div>
              <p className="text-white/40 text-xs leading-relaxed max-w-xs">
                NOT the Nevada Department of Motor Vehicles. All forms link to official Nevada DMV sources (dmv.nv.gov). Licensed third-party document preparation service (NRS240A).
              </p>
            </div>
            <div className="flex flex-wrap gap-8">
              <div>
                <p className="text-white/30 text-xs uppercase tracking-widest font-bold mb-3">Quick Links</p>
                <ul className="space-y-2">
                  {[['Home', '/'], ['Our Services', '/services/vehicle-registration'], ['Fees & Pricing', '/fees'], ['FAQ', '/faq'], ['Contact Us', '/contact']].map(([label, to], i) => (
                    <li key={i}><Link to={to} className="text-white/50 hover:text-white text-xs transition-colors">{label}</Link></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-white/30 text-xs uppercase tracking-widest font-bold mb-3">Contact</p>
                <ul className="space-y-2">
                  <li><a href="tel:7028028696" className="text-white/50 hover:text-white text-xs transition-colors font-semibold">702.802.8696</a></li>
                  <li><a href="mailto:info@dmv-xpress.com" className="text-white/50 hover:text-white text-xs transition-colors">info@dmv-xpress.com</a></li>
                  <li><span className="text-white/30 text-xs">4525 S Sandhill Rd, Las Vegas NV</span></li>
                  <li><a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="text-white/40 hover:text-white/50 text-xs transition-colors">Nevada DMV (Official) ↗</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="pt-5 flex flex-col sm:flex-row justify-between items-center gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-white/25 text-xs">© 2025 DMV-Xpress Services™. All rights reserved. Licensed Nevada Document Preparation Service.</p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer nofollow" className="text-white/25 hover:text-white/50 text-xs transition-colors">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
